<script setup lang="ts">
import { storeToRefs } from 'pinia';

import useBuyNft from '@/composables/web3/useBuyNft.client';
import { CartStateEnum, useCartStore } from '@/stores/cart/useCartStore';
import { useTransactionStore } from '@/stores/transaction/useTransactionStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { watch } from '#imports';

type Props = {
    isMobile: boolean;
};

defineProps<Props>();

const cartStore = useCartStore();

const { transactions } = storeToRefs(useTransactionStore());

const { setCartState } = cartStore;

const {
    cart,
    cartState
} = storeToRefs(cartStore);

const userStore = useUserStore();

const { user } = storeToRefs(userStore);

const {
    buyNFT,
    loadingState,
    AsyncState
} = useBuyNft();

const isLoading = computed(() => Object.values(loadingState.value).some(value => value === AsyncState.IS_LOADING));

const handleToPayment = () => {
    if (user.value) {
        setCartState({ state: CartStateEnum.PAYMENT });
    }
    else {
        setCartState({ visible: false });
        userStore.showUserIsDisconnect();
    }
};

const attemptBuyNft = async () => {
    const resultTransactions = await buyNFT();
    if (resultTransactions && resultTransactions.length) {
        setCartState({ state: CartStateEnum.TRANSACTIONS });
    }
};

watch(
    [
        cartState,
        user
    ],
    ([
        stateValue,
        userValue
    ]) => {
        if (stateValue.state === CartStateEnum.PAYMENT && !userValue) {
            setCartState({ state: CartStateEnum.CART });
        }
    }
);

</script>

<template>
  <Dropdown :is-open="cartState.visible"
            :position="isMobile ? 'headerFixed' : 'bottomLeft'"
            @update:is-open="(val: boolean) => setCartState({ visible: val })"
  >
    <template #toggle>
      <ClientOnly>
        <template #fallback>
          <div type="button"
               role="button"
               aria-label="My cart"
               :title="cart.size > 0 ? 'Review your items' : 'Your cart is empty'"
               class="cart-button display-flex justify-center align-items-center border-500"
               :class="cartState.visible && 'async-cart-button--active'"
               @click="() => setCartState({ visible: !cartState.visible })"
          >
            <IconMain icon="cart"
                      :size="24"
            />
            <div v-if="cart.size"
                 class="cart-button__amount display-flex justify-center align-items-center"
            >
              {{ cart.size }}
            </div>
          </div>
        </template>
        <div type="button"
             role="button"
             aria-label="My cart"
             :title="cart.size > 0 ? 'Review your items' : 'Your cart is empty'"
             class="cart-button display-flex justify-center align-items-center border-500"
             :class="cartState.visible && 'async-cart-button--active'"
             @click="() => setCartState({ visible: !cartState.visible })"
        >
          <IconMain icon="cart"
                    :size="24"
          />
          <div v-if="cart.size"
               class="cart-button__amount display-flex justify-center align-items-center"
          >
            {{ cart.size }}
          </div>
        </div>
      </ClientOnly>
    </template>
    <template #content>
      <div class="cart-wrapper display-flex">
        <Transition name="slide-fade"
                    mode="out-in"
        >
          <div v-if="!cart.size && !transactions.length"
               class="grow width-full"
          >
            <CartEmptyState />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.CART"
               class="grow width-full"
          >
            <Cart @to-payment="handleToPayment" />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.PAYMENT"
               class="grow width-full"
          >
            <CartPayment :is-payment-loading="isLoading"
                         @to-cart="setCartState({ state: CartStateEnum.CART })"
                         @on-pay="attemptBuyNft()"
            />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.TRANSACTIONS"
               class="grow width-full"
          >
            <CartTransactions />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.SUCCESS"
               class="grow width-full"
          >
            <CartSuccess />
          </div>
        </Transition>
      </div>
    </template>
  </Dropdown>
</template>

<style scoped lang="scss">

.async-cart-button--active {
  background-color: var(--bg-secondary);
}

.cart {
  &-button {
    position: relative;
    width: var(--control-500-size-single, 52px);
    height: var(--control-500-size-single, 52px);
    transition: background-color 0.3s ease;
    cursor: pointer;

    &__amount {
      position: absolute;
      top: -5px;
      right: -5px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: var(--bg-brand-aviation-primary);
    }
  }
}

.cart-wrapper {
  width: 348px;
  padding: $spacing-400 $spacing-600 $spacing-400 $spacing-600;
  border-radius: var(--background-500-radius, 4px);
  background-color: var(--layer-floor-2);

  @include tablet-768 {
    width: 100%;
    height: 100%;
    padding-right: $spacing-400;
    padding-left: $spacing-400;
    background-color: var(--layer-floor-1);
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.5s cubic-bezier(1, 0.8, 0.9, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(2px);
  opacity: 0;
}
</style>
